import { useEffect, useId, useRef, useState } from 'react';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Text } from '../atoms/Typography/Text';
import { VoiceRecorderBar } from '../atoms/VoiceRecorderButton';
import { useTranslation } from '../../i18n/useTranslation';
import { useOptionalCapture, type CaptureContextValue } from './CaptureContext';
import { NoteReadyBanner } from './NoteReadyBanner';
import './CaptureDock.css';

type Platform = 'ios' | 'android' | 'desktop';

function detectPlatform(): Platform {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

/** Inside the Capacitor app shell "Open settings" can reach the system settings. */
function isNativeApp(): boolean {
  return typeof window !== 'undefined' && 'Capacitor' in window;
}

/** Microphone blocked or no recording support (wireframe "Voice Recorder Permission Denied"). */
function PermissionDeniedNotice({ capture }: { capture: CaptureContextValue }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const helpId = useId();
  const [showHelp, setShowHelp] = useState(false);
  const unsupported = capture.notice === 'unsupported';

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <Alert
      ref={ref}
      tabIndex={-1}
      tone="warning"
      title={t('voice.recorder.deniedTitle')}
      action={
        <Button variant="secondary" size="sm" onClick={capture.writeInstead}>
          {t('voice.recorder.writeInstead')}
        </Button>
      }
      onDismiss={capture.dismissNotice}
      dismissLabel={String(t('voice.ready.dismiss'))}
      data-testid="voice-permission-denied"
    >
      <span className="capture-dock__stack">
        <span>{unsupported ? t('voice.recorder.unsupported') : t('voice.recorder.deniedBody')}</span>
        {!unsupported && (
          <span className="capture-dock__inline">
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={showHelp}
              aria-controls={helpId}
              onClick={() => setShowHelp((v) => !v)}
            >
              {t('voice.recorder.deniedHelp')}
            </Button>
            {isNativeApp() && (
              <Button variant="secondary" size="sm" onClick={() => window.location.assign('app-settings:')}>
                {t('voice.recorder.openSettings')}
              </Button>
            )}
          </span>
        )}
        <span id={helpId} hidden={!showHelp}>
          {t(`voice.recorder.help.${detectPlatform()}`)}
        </span>
        <Text as="span" variant="caption" color="inherit">
          {t('voice.recorder.dictationHint')}
        </Text>
      </span>
    </Alert>
  );
}

/**
 * Everything voice notes show on top of a page (rendered by AppLayout): the
 * recorder bar, pinned above the mobile tab bar (bottom right on desktop), the
 * "Note ready" banner and the recorder's notices. Pages get a matching
 * scroll-padding so a focused element is never hidden behind it (WCAG 2.4.11).
 */
export function CaptureDock() {
  const { t } = useTranslation();
  const capture = useOptionalCapture();
  const dockRef = useRef<HTMLDivElement>(null);

  const visible =
    capture !== null &&
    (capture.phase !== 'idle' || capture.notice !== null || capture.ready !== null);

  useEffect(() => {
    const dock = dockRef.current;
    if (!visible || !dock || typeof ResizeObserver === 'undefined') return undefined;
    const root = document.documentElement;
    const observer = new ResizeObserver(() => {
      root.style.scrollPaddingBottom = `${dock.getBoundingClientRect().height}px`;
    });
    observer.observe(dock);
    return () => {
      observer.disconnect();
      root.style.scrollPaddingBottom = '';
    };
  }, [visible]);

  if (!capture || !visible) return null;
  const { phase, notice } = capture;
  const barPhase = phase === 'recording' || phase === 'paused' || phase === 'uploading' || phase === 'processing' ? phase : null;
  const languageName = (() => {
    try {
      return new Intl.DisplayNames([capture.language], { type: 'language' }).of(capture.language) ?? capture.language;
    } catch {
      return capture.language;
    }
  })();

  return (
    <div ref={dockRef} className="capture-dock" data-testid="capture-dock">
      {(notice === 'denied' || notice === 'unsupported') && <PermissionDeniedNotice capture={capture} />}
      {notice === 'noCredits' && (
        <Alert
          tone="warning"
          title={t('voice.recorder.noCredits')}
          onDismiss={capture.dismissNotice}
          dismissLabel={String(t('voice.ready.dismiss'))}
          action={
            <span className="capture-dock__inline">
              <Button variant="secondary" size="sm" onClick={capture.recordAnyway}>
                {t('voice.recorder.recordAnyway')}
              </Button>
              <Button variant="ghost" size="sm" onClick={capture.writeInstead}>
                {t('voice.recorder.writeInstead')}
              </Button>
            </span>
          }
        >
          {t('voice.recorder.noCreditsBody')}
        </Alert>
      )}
      {notice === 'uploadFailed' && (
        <Alert
          title={t('voice.recorder.uploadFailedTitle')}
          onDismiss={capture.dismissNotice}
          dismissLabel={String(t('voice.ready.dismiss'))}
        >
          {t('voice.recorder.uploadFailedBody')}
        </Alert>
      )}
      {notice === 'storageUnavailable' && (
        <Alert tone="warning" onDismiss={capture.dismissNotice} dismissLabel={String(t('voice.ready.dismiss'))}>
          {t('voice.queue.storageUnavailable')}
        </Alert>
      )}
      <NoteReadyBanner />
      {barPhase !== null && (
        <VoiceRecorderBar
          phase={barPhase}
          elapsedMs={capture.elapsedMs}
          level={capture.level}
          lowSignal={capture.lowSignal}
          limitWarning={capture.limitWarning}
          maxDurationSec={capture.settings?.maxDurationSec ?? 180}
          credits={capture.noteCredits}
          languageLabel={languageName}
          onStop={capture.stopRecording}
          onPause={capture.pauseRecording}
          onResume={capture.resumeRecording}
          onDiscard={capture.discardRecording}
          onLanguage={capture.cycleLanguage}
          onRecordAnother={() => capture.startRecording()}
          onClose={capture.dismissProcessing}
        />
      )}
    </div>
  );
}
